import { Injectable } from '@angular/core';
import { PublicPartnerApiService } from './../../../app/@shared/services/publicpartnerapi.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, withLatestFrom, catchError, concatMap, switchMap, filter, tap } from 'rxjs/operators';
import { getAuth } from '../auth/auth.selectors';
import { globalFailure } from '../common.actions';
import * as BreakoutActions from './breakouts.actions';
import * as BreakoutSelectors from './breakouts.selectors';
import * as VirtualSwitchesActions from './../../../app/@store/virtual_switches/virtual_switches.actions';
import * as _ from 'lodash';
import { Observable, of } from 'rxjs';
import { BreakoutGetResponse, BreakoutsGetResponse, BreakoutVirtualSwitchGetResponse } from './../../../app/@shared/models/breakout.model';
import { HttpParams } from '@angular/common/http';
import { selectRouteParam } from '../router/router.selector';
import { SDNService } from './../../../app/panel/accounts/sdn/sdn.service';
import { IRequestOptions } from '../request-options.interface';

@Injectable()
export class BreakoutsEffects {
  constructor(private readonly actions$: Actions, private readonly store: Store, private readonly api: PublicPartnerApiService, private readonly SDNService: SDNService) { }

  loadBreakouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.loadBreakouts),
      withLatestFrom(
        this.store.select(getAuth),
        this.store.select(BreakoutSelectors.getListConfig),
        this.store.select(selectRouteParam('account_uuid'))
      ),
      switchMap(([action, auth, config, accountUuid]) => {
        return this.SDNService.getSdnFilterBreakout$().pipe(
          map(sdnFilter => {
            let newFilter = _.get(config, 'filter', '');
            if (!_.isEmpty(sdnFilter)) {
              if (!_.isEmpty(newFilter)) {
                newFilter += ',';
              }
              newFilter += sdnFilter;
            }

            const newConfig: IRequestOptions = {
              ...config,
              filter: newFilter
            };
            if (newConfig.filter?.length === 0) {
              newConfig.filter = undefined;
            }
            return [action, auth, newConfig, accountUuid] as [typeof action, any, IRequestOptions, string];
          })
        )
      }),
      concatMap(([action, auth, config, accountUuid]) => {
        return this.api.get<BreakoutsGetResponse>('/partners/' + auth.partner_uuid + '/breakouts', new HttpParams({ fromObject: { ...config } })).pipe(
          map(result => accountUuid
            ? BreakoutActions.loadBreakoutsForContractSuccess({
              accountUuid,
              data: result.breakouts,
              meta: result._meta,
              links: result._links
            })
            : BreakoutActions.loadBreakoutsSuccess({
              data: result.breakouts,
              meta: result._meta,
              links: result._links
            })
          ),
          catchError(error => of(
            accountUuid
              ? BreakoutActions.loadBreakoutsForContractFailure({
                accountUuid,
                error
              })
              : BreakoutActions.loadBreakoutsFailure({
                error
              }),
            globalFailure({ error })))
        )
      })
    )
  );

  loadBreakoutsNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.loadBreakoutsNext),
      withLatestFrom(
        this.store.select(BreakoutSelectors.getBreakoutsLinks),
        this.store.select(selectRouteParam('account_uuid'))
      ),

      filter(([_, links]) => links !== undefined && links.next !== undefined),
      concatMap(([action, links, accountUuid]) => {
        const url = new URL(links.next!.href);
        return this.api.get<BreakoutsGetResponse>(url.pathname, {
          page: url.searchParams.get('page') || '0',
          limit: url.searchParams.get('limit') || '10',
          sort: url.searchParams.get('sort') || '',
          fields: url.searchParams.get('fields')?.split(',') || [],
        }).pipe(
          map((result) => accountUuid
            ? BreakoutActions.loadVBreakoutsForContractNextSuccess({
              accountUuid,
              data: result.breakouts || {},
              meta: result._meta,
              links: result._links
            })
            : BreakoutActions.loadVBreakoutsNextSuccess({
              data: result.breakouts || {},
              meta: result._meta,
              links: result._links
            }),
          ),
          catchError(error => of(
            accountUuid ? BreakoutActions.loadBreakoutsForContractNextFailure({ accountUuid, error }) : BreakoutActions.loadBreakoutsNextFailure({ error }),
            globalFailure({ error }))
          )
        )
      })
    )
  );

  loadBreakout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.loadBreakout),
      withLatestFrom(
        this.store.select(getAuth),
      ),
      concatMap(([action, auth]) => {
        return this.api.get<BreakoutGetResponse>('/partners/' + auth!.partner_uuid + '/breakouts/' + action.breakout_uuid).pipe(
          map(result => BreakoutActions.loadBreakoutSuccess({
            data: result.breakout,
            breakout_uuid: action.breakout_uuid,
          })),
          catchError(error => of(BreakoutActions.loadBreakoutFailure({
            breakout_uuid: action.breakout_uuid,
            error
          }), globalFailure({ error })))
        )
      })
    )
  );

  loadSelectedBreakout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.loadSelectedBreakout),
      withLatestFrom(
        this.store.select(getAuth),
        this.store.select(selectRouteParam('breakout_uuid')),
      ),
      tap(([action, auth, breakout_uuid]) => {
        this.store.dispatch(BreakoutActions.loadSelectedBreakoutStart({ breakout_uuid }));
      }),
      concatMap(([action, auth, breakout_uuid]) => {
        return this.api.get<BreakoutGetResponse>('/partners/' + auth!.partner_uuid + '/breakouts/' + breakout_uuid).pipe(
          map(result => BreakoutActions.loadSelectedBreakoutSuccess({
            data: result.breakout,
            breakout_uuid,
          })),
          catchError(error => of(BreakoutActions.loadSelectedBreakoutFailure({
            breakout_uuid,
            error
          }), globalFailure({ error })))
        )
      })
    )
  );

  createBreakout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.createBreakout),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.post<{ request_uuid: string; object_uuid: string; }>('/partners/' + auth!.partner_uuid + '/breakouts', {
          name: action.name,
          labels: action.labels,
          project_uuid: action.project_uuid
        }).pipe(
          map(result => BreakoutActions.createBreakoutSuccess({
            breakout_uuid: result.object_uuid
          })),
          catchError(error => of(BreakoutActions.createBreakoutFailure({
            error
          }), globalFailure({ error })))
        )
      })
    )
  );

  patchBreakout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.patchBreakout),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.patch<void>('/partners/' + auth!.partner_uuid + '/breakouts/' + action.breakout_uuid, _.omit(action, 'breakout_uuid')).pipe(
          map(result => BreakoutActions.patchBreakoutSuccess({
            breakout_uuid: action.breakout_uuid
          })),
          catchError(error => of(BreakoutActions.patchBreakoutFailure({
            breakout_uuid: action.breakout_uuid,
            error
          }), globalFailure({ error })))
        )
      })
    )
  );

  deleteBreakout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.deleteBreakout),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.delete<void>('/partners/' + auth!.partner_uuid + '/breakouts/' + action.breakout_uuid).pipe(
          map(result => BreakoutActions.deleteBreakoutSuccess({
            breakout_uuid: action.breakout_uuid
          })),
          catchError(error => of(BreakoutActions.deleteBreakoutFailure({
            breakout_uuid: action.breakout_uuid,
            error
          }), globalFailure({ error })))
        )
      })
    )
  );

  loadBreakoutVirtualSwitches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.loadBreakoutVirtualSwitches),
      withLatestFrom(this.store.select(getAuth)),
      switchMap(([action, auth]) =>
        this.store.select(BreakoutSelectors.getVirtualSwitchesListConfig({ breakout_uuid: action.breakout_uuid })).pipe(
          map(listConfig => [action, auth, listConfig] as [typeof action, any, IRequestOptions]),
        )
      ),
      concatMap(([action, auth, config]) => {
        return this.api.get<BreakoutVirtualSwitchGetResponse>('/partners/' + auth.partner_uuid + '/breakouts/' + action.breakout_uuid + '/virtual_switches' /*, config as HttpParams*/).pipe(
          map(result => {
            if ((result as unknown as any).virtual_switches !== undefined && result.breakout_virtual_switches === undefined) {
              result.breakout_virtual_switches = (result as unknown as any).virtual_switches;
            }
            return result;
          }),
          map(result => BreakoutActions.loadBreakoutVirtualSwitchesSuccess({
            breakout_uuid: action.breakout_uuid,
            data: _.keyBy(
              _.map(result.breakout_virtual_switches, virtualSwitch => ({
                ...virtualSwitch,
                object_uuid: virtualSwitch.virtual_switch_uuid,
                name: virtualSwitch.virtual_switch_name
              })),
              s => s.virtual_switch_uuid
            ),
            meta: {
              total: _.size(result.breakout_virtual_switches),
              limit: _.size(result.breakout_virtual_switches),
              page: 0,
              count: _.size(result.breakout_virtual_switches),
              offset: 0
            },
            links: {}
            // meta: {result._meta}, // TODO: use the real meta/links when BUG-3761 is resolved
            // links: result._links
          })),
          catchError(error => of(BreakoutActions.loadBreakoutVirtualSwitchesFailure({
            breakout_uuid: action.breakout_uuid,
            error
          }), globalFailure({ error })))
        )
      })
    )
  );

  addBreakoutVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.addBreakoutVirtualSwitch),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.post<any>('/partners/' + auth!.partner_uuid + '/breakouts/' + action.breakout_uuid + '/virtual_switches', { virtual_switch_uuid: action.virtual_switch_uuid }).pipe(
          map((result) => {
            return BreakoutActions.addBreakoutVirtualSwitchSuccess({
              breakout_uuid: action.breakout_uuid,
              virtual_switch_uuid: action.virtual_switch_uuid,
            });
          }),
          catchError(error => of(BreakoutActions.addBreakoutVirtualSwitchFailure({
            breakout_uuid: action.breakout_uuid,
            virtual_switch_uuid: action.virtual_switch_uuid,
            error
          }), globalFailure({ error })))
        ) as Observable<any>;
      })
    )
  );

  patchBreakoutVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.patchBreakoutVirtualSwitch),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.patch<any>('/partners/' + auth!.partner_uuid + '/breakouts/' + action.breakout_uuid + '/virtual_switches/' + action.virtual_switch_uuid, action.data as HttpParams).pipe(
          map((result) => {
            return BreakoutActions.patchBreakoutVirtualSwitchSuccess({
              breakout_uuid: action.breakout_uuid,
              virtual_switch_uuid: action.virtual_switch_uuid,
            });
          }),
          catchError(error => of(BreakoutActions.patchBreakoutVirtualSwitchFailure({
            breakout_uuid: action.breakout_uuid,
            virtual_switch_uuid: action.virtual_switch_uuid,
            error
          }), globalFailure({ error })))
        ) as Observable<any>;
      })
    )
  );

  deleteBreakoutVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.deleteBreakoutVirtualSwitch),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.delete<any>('/partners/' + auth!.partner_uuid + '/breakouts/' + action.breakout_uuid + '/virtual_switches/' + action.virtual_switch_uuid).pipe(
          map((result) => {
            return BreakoutActions.deleteBreakoutVirtualSwitchSuccess({
              breakout_uuid: action.breakout_uuid,
              virtual_switch_uuid: action.virtual_switch_uuid,
            });
          }),
          catchError(error => of(BreakoutActions.deleteBreakoutVirtualSwitchFailure({
            breakout_uuid: action.breakout_uuid,
            virtual_switch_uuid: action.virtual_switch_uuid,
            error
          }), globalFailure({ error })))
        ) as Observable<any>;
      })
    )
  );

  reloadBreakouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BreakoutActions.createBreakoutSuccess,
        BreakoutActions.patchBreakoutSuccess,
        BreakoutActions.deleteBreakoutSuccess,
      ),
      map(action => BreakoutActions.loadBreakouts())
    )
  );

  reloadBreakout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BreakoutActions.patchBreakoutSuccess,
      ),
      map(action => BreakoutActions.loadBreakout({ breakout_uuid: action.breakout_uuid }))
    )
  );

  reloadBreakoutVirtualSwitches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BreakoutActions.deleteBreakoutVirtualSwitchSuccess,
        BreakoutActions.addBreakoutVirtualSwitchSuccess,
        BreakoutActions.patchBreakoutVirtualSwitchSuccess
      ),
      map(action => BreakoutActions.loadBreakoutVirtualSwitches({ breakout_uuid: action.breakout_uuid }))
    )
  );

  reloadVirtualSwitchesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BreakoutActions.deleteBreakoutVirtualSwitchSuccess,
        BreakoutActions.addBreakoutVirtualSwitchSuccess,
        BreakoutActions.patchBreakoutVirtualSwitchSuccess
      ),
      map(action => VirtualSwitchesActions.loadVirtualSwitches())
    )
  );


  setSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.setSearch),
      withLatestFrom(
        this.store.select(selectRouteParam('account_uuid'))
      ),
      map(([action, accountUuid]) => accountUuid
        ? BreakoutActions.setSearchForAccount({ search: action.search, account_uuid: accountUuid })
        : BreakoutActions.setSearchGlobal({ search: action.search })
      )
    )
  );

  setListConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BreakoutActions.setListConfig),
      withLatestFrom(
        this.store.select(selectRouteParam('account_uuid'))
      ),
      map(([action, accountUuid]) => accountUuid
        ? BreakoutActions.setListConfigForAccount({ config: action.config, account_uuid: accountUuid })
        : BreakoutActions.setListConfigGlobal({ config: action.config })
      )
    )
  );
}
